import { z } from "zod";

export type QuestCandleData = [timestamp: number, open: number, close: number, min: number, max: number];

const QuestCandleDataSchema = z.tuple([z.number(), z.number(), z.number(), z.number(), z.number()]);

const ErrorEventSchema = z.tuple([z.number(), z.string()]);

export type QuestApi = ReturnType<typeof createQuestApi>;

const QuestQuerySchema = z.object({
  sample: z.enum(["10s", "1m", "5m", "15m", "1h", "4h", "1d"]),
  id: z.string(),
  period_from: z.string().min(3),
  from: z.number(),
  to: z.number(),
  field: z.string(),
  isCalc: z.boolean(),
});

type QuestQuery = z.infer<typeof QuestQuerySchema>;

const StringsArraySchema = z.array(z.string());

type GetToken = () => Promise<string | undefined>;

export const createQuestApi = ({ baseUrl, getToken }: { baseUrl: string; getToken: GetToken }) => {
  const buildHeaders = async (): Promise<Record<string, string>> => ({
    Authorization: `Bearer ${(await getToken()) ?? ""}`,
    "Content-Type": "application/json",
  });
  return {
    historical: createFetchHistorical({ buildHeaders, url: baseUrl }),
    live: createFetchLive({ buildHeaders, url: baseUrl }),
    products: async (options?: { signal?: AbortSignal }) =>
      fetch(`${baseUrl}/api/v2/products`, {
        headers: await buildHeaders(),
        signal: options?.signal,
      })
        .then((x) => x.json())
        .then((x) => StringsArraySchema.parseAsync(x)),
    periods: async (productId: string, options?: { signal?: AbortSignal }) =>
      fetch(`${baseUrl}/api/v2/products/${productId}/periods`, {
        headers: await buildHeaders(),
        signal: options?.signal,
      })
        .then((x) => x.json())
        .then((x) => StringsArraySchema.parseAsync(x)),
  };
};

const QuestCandleDataArraySchema = z.array(QuestCandleDataSchema).transform((x): Array<QuestCandleData> => x);

type BuildHeaders = () => Promise<Record<string, string>>;

const createFetchHistorical =
  ({ buildHeaders, url: baseUrl }: { buildHeaders: BuildHeaders; url: string }) =>
  async (query: QuestQuery, options?: { signal?: AbortSignal }) => {
    await QuestQuerySchema.parseAsync(query);
    await checkRetry(query);
    const url = new URL(
      `${baseUrl}/api/v2/products/${query.id}/periods/${query.period_from}/sample/${query.sample}/prices`,
    );
    url.searchParams.append("from", query.from.toString());
    url.searchParams.append("to", query.to.toString());
    url.searchParams.append("field", query.field);
    url.searchParams.append("isCalc", query.isCalc.toString());

    const response = await fetch(url.toString(), {
      method: "GET",
      headers: await buildHeaders(),
      signal: options?.signal,
    });

    if (response.status === 422) {
      return { data: [], ended: true };
    }

    await validateResponse(response, "historical", query);
    const data = await response.json();
    return {
      data: await QuestCandleDataArraySchema.parseAsync(data),
      ended: false,
    };
  };

// Default ceiling on how long the live stream may go without a chunk before we treat the socket as dead. Callers pass a sample-tuned value; this is the floor.
const DEFAULT_LIVE_IDLE_TIMEOUT_MS = 60_000;

// Thrown when the live stream's watchdog fires. Distinct from a network/abort error so consumers (and logs) can tell the connection silently dying apart from a normal failure or an intentional cancellation.
export class LiveStreamIdleError extends Error {
  constructor(query: QuestQuery, idleMs: number) {
    super(`Live stream ${query.id}:${query.period_from} idle for ${idleMs}ms — assuming dead connection`);
    this.name = "LiveStreamIdleError";
  }
}

const createFetchLive = ({ buildHeaders, url: baseUrl }: { buildHeaders: BuildHeaders; url: string }) =>
  async function* (query: QuestQuery, options?: { signal?: AbortSignal; idleTimeoutMs?: number }) {
    await QuestQuerySchema.parseAsync(query);
    await checkRetry(query);
    const url = new URL(
      `${baseUrl}/api/v2/products/${query.id}/periods/${query.period_from}/sample/${query.sample}/current/stream`,
    );
    url.searchParams.append("from", query.from.toString());
    url.searchParams.append("to", query.to.toString());
    url.searchParams.append("field", query.field);
    url.searchParams.append("isCalc", query.isCalc.toString());

    const idleTimeoutMs = options?.idleTimeoutMs ?? DEFAULT_LIVE_IDLE_TIMEOUT_MS;

    // Watchdog: an OS suspend can leave this stream's socket half-open — the connection is dead but `reader.read()` neither resolves nor rejects, so the query would sit "fetching" forever and the candle goes stale with no error to retry on. We drive the fetch with our own controller and abort it if no data arrives within the idle window, surfacing a real error so the caller's retry can reconnect. The external (React Query) signal is chained in so query cancellation still aborts the fetch.
    const controller = new AbortController();
    const onExternalAbort = () => controller.abort(options?.signal?.reason);
    if (options?.signal) {
      if (options.signal.aborted) controller.abort(options.signal.reason);
      else options.signal.addEventListener("abort", onExternalAbort, { once: true });
    }

    let idleTimer: ReturnType<typeof setTimeout> | undefined;
    let timedOut = false;
    const armIdleTimer = () => {
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, idleTimeoutMs);
    };

    try {
      const response = await fetch(url.toString(), {
        method: "GET",
        headers: await buildHeaders(),
        signal: controller.signal,
      });

      await validateResponse(response, "live", query);

      const reader = response.body?.getReader();
      if (!reader) {
        return;
      }

      const decoder = new TextDecoder();
      armIdleTimer();
      try {
        while (true) {
          let result: ReadableStreamReadResult<Uint8Array>;
          try {
            result = await reader.read();
          } catch (e) {
            // The watchdog aborted us so we surface a real, retryable idle error rather than a generic abort (which the caller treats as a silent cancel).
            if (timedOut) return Promise.reject(new LiveStreamIdleError(query, idleTimeoutMs));
            throw e;
          }
          const { done, value } = result;
          if (done) break;
          armIdleTimer(); // Got data, the connection is alive, so reset the watchdog.
          const text = decoder.decode(value);
          try {
            for (const line of text
              .split(/data:/)
              .map((x) => x.trim())
              .filter(Boolean)) {
              const data = JSON.parse(line);
              const parsed = QuestCandleDataArraySchema.safeParse(data);
              if (parsed.success) yield parsed.data;
              else {
                const error = ErrorEventSchema.safeParse(data);
                if (error.success) {
                  const err = new Error(error.data[1]);
                  console.error(err);
                  return Promise.reject(new Error(error.data[1]));
                }
                console.error("Invalid data", parsed.error, { data });
              }
            }
          } catch (e) {
            console.error("Invalid data", e);
            return Promise.reject(e);
          }
        }
      } finally {
        // Release the socket if the consumer stops early (e.g. on reconnect).
        reader.cancel().catch(() => {});
      }
    } finally {
      if (idleTimer) clearTimeout(idleTimer);
      options?.signal?.removeEventListener("abort", onExternalAbort);
    }
  };

const StopRetry = new Map<string, string>();
function buildRetryKey(query: QuestQuery) {
  return `${query.id}:${query.period_from}`;
}

const checkRetry = (query: QuestQuery) => {
  if (StopRetry.has(buildRetryKey(query))) {
    return Promise.reject(
      new Error(StopRetry.get(buildRetryKey(query)) || `Quest ${query.id}:${query.period_from} unavailable`),
    );
  }
};

const validateResponse = async (response: Response, name: string, query: QuestQuery) => {
  if (response.status === 503) {
    return Promise.reject(new Error(`Quest ${name} unavailable`));
  }

  // As per https://linear.app/artis-works/issue/WEB2-938/charts-unretry-able-error-codes
  // We should not retry the following errors:
  // 400 Bad request: invalid query (curve unavailable, invalid field, etc.)
  // 401 Unauthorized (token expired or invalid)
  // 403 Forbidden: user does not have permissions (user does not have access to the product)
  // 404 Not found: product-month data is not in questdb (contract expired)
  const errors: Record<number, string> = {
    400: "Bad Request: The request was invalid or cannot be served.",
    401: "Unauthorized: Authentication is required and has failed or has not yet been provided.",
    403: "Forbidden: You don't have access to this resource.",
    404: `Missing product (${query.id}) in month ${query.period_from}`,
  };

  const error = errors?.[response.status];
  if (error) {
    StopRetry.set(buildRetryKey(query), error);
    return Promise.reject(new Error(error));
  }

  if (!response.ok) {
    const errorText = await response.text();
    return Promise.reject(new Error(`Failed to fetch data: ${errorText}`));
  }
};

export const errorMessageToHumanReadable = (message: string) => {
  if (message.includes("Bad Request"))
    return {
      code: 400,
      title: "Curve unavailable",
      description: "A Curve you have selected can no longer be plotted.",
      contact: true,
    };
  if (message.includes("Unauthorized"))
    return {
      code: 401,
      title: "Session expired",
      description: "Your session has expired. Please log out and back in.",
      contact: false,
    };
  if (message.includes("Forbidden"))
    return {
      code: 403,
      title: "Missing permissions",
      description: "You do not have the correct permissions to view a Curve in this chart.",
      contact: true,
    };
  if (message.includes("Missing product"))
    return {
      code: 404,
      title: "Contract expired",
      description: "The selected Curve and Period is now expired.",
      contact: true,
    };
  return null;
};
